'use client'

import React, { createContext, ReactNode, useContext, useMemo, useState } from 'react'
import { generateRandomKey } from '../../MyStichtingOpenHelpers/src'
import { NotificationModel, NotificationsNotifyOptions, NotificationVariant } from '../types'

export type NotificationProviderValue = {
    remove: (id: string) => void
    success: (options: NotificationsNotifyOptions) => void
    warning: (options: NotificationsNotifyOptions) => void
    error: (options: NotificationsNotifyOptions) => void
    info: (options: NotificationsNotifyOptions) => void
    notifications: NotificationModel[]
}

export const NotificationContext = createContext<NotificationProviderValue>({
    remove: () => null,
    success: () => null,
    warning: () => null,
    error: () => null,
    info: () => null,
    notifications: [],
})

/** @deprecated use useContext(NotificationContext) */
export const useNotification = (): NotificationProviderValue => {
    return useContext(NotificationContext)
}

interface NotificationProviderProps {
    children: ReactNode
}

/**
 * This provider is responsible for receiving and showing fixed notifications.
 */
export const NotificationProvider = ({ children }: NotificationProviderProps) => {
    const [notifications, setNotifications] = useState<NotificationModel[]>([])

    const notify = (options: NotificationsNotifyOptions) => {
        const id = generateRandomKey()
        const newNotifications = [
            ...notifications,
            {
                id,
                ...options,
                variant: options.variant ?? NotificationVariant.INFO,
            },
        ]
        setNotifications(newNotifications)
    }

    const success = (options: NotificationsNotifyOptions) => {
        notify({ ...options, variant: NotificationVariant.SUCCESS })
    }

    const warning = (options: NotificationsNotifyOptions) => {
        notify({ ...options, variant: NotificationVariant.WARNING })
    }

    const error = (options: NotificationsNotifyOptions) => {
        notify({ ...options, variant: NotificationVariant.ERROR })
    }

    const info = (options: NotificationsNotifyOptions) => {
        notify({ ...options, variant: NotificationVariant.INFO })
    }

    const remove = (id: string) => {
        const newNotifications = notifications.filter((notification) => notification.id !== id)
        setNotifications(newNotifications)
    }

    const providerValue = useMemo(
        () => ({
            remove,
            success,
            warning,
            error,
            info,
            notifications,
        }),
        [notifications],
    )

    return <NotificationContext.Provider value={providerValue}>{children}</NotificationContext.Provider>
}
